// Проблем с продукт от доставката: маркиране с причина и бележка, и изборът при
// запис, когато има маркирани. Маркираният продукт НЕ блокира работата — при
// запис избираш: задръж цялата доставка (остава чернова със статус
// „Задържана") или запиши без проблемните (останалите се заприходяват сега).
import { useEffect, useState } from "react";
import { Modal } from "antd";
import { FlagOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../../utils/modalStyles";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { ConfirmDialog } from "../../components/confirm/ConfirmDialog";
import {
  DANGER_BUTTON_CLASS,
  LINK_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../../components/list/buttonStyles";

const PROBLEM_REASON_KEYS = ["damaged", "short", "wrong", "expired", "other"] as const;

// Текстът, който се пази на реда: „Повредена · 3 бутилки счупени"
const composeProblem = (reasonLabel: string, note: string) =>
  [reasonLabel, note.trim()].filter(Boolean).join(" · ");

export const DeliveryProblemModal = ({
  open,
  productName,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  productName: string;
  current: string | null;
  onClose: () => void;
  onSave: (problem: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [reason, setReason] = useState<string>("damaged");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    setReason("damaged");
    setNote("");
  }, [open]);
  const reasonLabel = (key: string) => t(`inventoryPage.problemReason_${key}`);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={500}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      <div className="flex flex-col bg-surface">
        <div className="flex flex-col gap-[3px] border-b border-shade-2 px-[22px] py-[18px]">
          <span className="text-[17px] font-semibold text-ink">{t("inventoryPage.problemTitle")}</span>
          <span className="text-[13px] text-body">{productName}</span>
        </div>
        <div className="flex flex-col gap-3.5 px-[22px] py-4">
          {current && (
            <span className="rounded-[7px] bg-danger-tint px-2.5 py-1.5 text-[12.5px] text-danger-ink">
              ⚑ {current}
            </span>
          )}
          <div role="radiogroup" className="flex flex-wrap gap-2">
            {PROBLEM_REASON_KEYS.map((key) => {
              const on = reason === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setReason(key)}
                  className={`cursor-pointer rounded-full border px-[13px] py-[7px] text-[13px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
                    on
                      ? "border-danger/45 bg-danger-tint font-medium text-danger-ink"
                      : "border-shade-3 bg-surface text-ink hover:border-shade-5"
                  }`}
                >
                  {reasonLabel(key)}
                </button>
              );
            })}
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
              {/* текстът отива в задържаната чернова, не в документа */}
              {t("inventoryPage.problemDescription")}
            </span>
            <textarea
              autoFocus
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t("inventoryPage.problemNotePh")}
              className={`resize-y rounded-lg border border-shade-3 bg-surface px-[11px] py-[9px] text-ink outline-none focus:border-brand ${
                isNarrow ? "text-base" : "text-sm"
              }`}
            />
          </label>
          <span className="text-[12.5px] leading-[1.45] text-body">{t("inventoryPage.problemHint")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-shade-2 px-[22px] pb-[18px] pt-3.5">
          {current && (
            <button type="button" onClick={() => onSave(null)} className={LINK_BUTTON_CLASS}>
              {t("inventoryPage.problemClear")}
            </button>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
            {t("inventoryPage.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onSave(composeProblem(reasonLabel(reason), note))}
            className={DANGER_BUTTON_CLASS}
          >
            {t("inventoryPage.problemMark")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Изборът при запис с маркирани продукти — общият потвърждаващ диалог с два
// избора-карти; всеки казва какво точно ще се заприходи. Фокусът (и Enter) е на
// „Задръж" — нищо не се заприходява, както досега.
export const DeliveryProblemSaveModal = ({
  open,
  items,
  restCount,
  onClose,
  onHold,
  onSaveWithout,
}: {
  open: boolean;
  items: { key: string; name: string; problem: string }[];
  // колко продукта се заприходяват при „Запиши без проблемните"
  restCount: number;
  onClose: () => void;
  onHold: () => void;
  onSaveWithout: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open={open}
      intent="warning"
      icon={<FlagOutlined />}
      title={t("inventoryPage.cfmProblemTitle", { count: items.length })}
      explanation={t("inventoryPage.cfmProblemText")}
      preview={{
        caption: t("inventoryPage.cfmProblemCaption"),
        items: items.map((item) => ({
          key: item.key,
          label: item.name,
          hint: `⚑ ${item.problem}`,
          tone: "danger",
        })),
      }}
      choices={[
        {
          key: "hold",
          label: t("inventoryPage.probSaveHold"),
          hint: t("inventoryPage.cfmProblemHoldHint"),
          primary: true,
          onSelect: onHold,
        },
        {
          key: "without",
          label: t("inventoryPage.probSaveWithout"),
          hint: t("inventoryPage.cfmProblemWithoutHint", { count: restCount }),
          onSelect: onSaveWithout,
        },
      ]}
      onCancel={onClose}
    />
  );
};
